import axios, { AxiosError } from 'axios'

export const api = axios.create({
  baseURL: '/api/v1',
  withCredentials: true, // отправлять crm_refresh cookie
  headers: { 'Content-Type': 'application/json' },
})

// In-memory access token (по решению 2026-05-24: НЕ localStorage против XSS)
let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken(): string | null {
  return accessToken
}

api.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

// Auto-refresh при 401: вызывает /auth/refresh с куки и перезапрашивает оригинал
let refreshPromise: Promise<string | null> | null = null

async function tryRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const r = await axios.post('/api/v1/auth/refresh', {}, { withCredentials: true })
        const newToken = r.data.access_token as string
        setAccessToken(newToken)
        return newToken
      } catch {
        setAccessToken(null)
        return null
      } finally {
        refreshPromise = null
      }
    })()
  }
  return refreshPromise
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError & { config?: { _retried?: boolean } }) => {
    const cfg = error.config
    if (
      error.response?.status === 401 &&
      cfg &&
      !cfg._retried &&
      !cfg.url?.includes('/auth/login') &&
      !cfg.url?.includes('/auth/refresh')
    ) {
      cfg._retried = true
      const newToken = await tryRefresh()
      if (newToken) {
        cfg.headers = cfg.headers ?? ({} as never)
        ;(cfg.headers as { Authorization?: string }).Authorization = `Bearer ${newToken}`
        return api.request(cfg)
      }
    }
    throw error
  },
)

export interface ApiError {
  error: {
    code: string
    message?: string
    fields?: { path: string; message: string }[]
    required_role?: string[]
    rule?: string
  }
}

export function isApiError(e: unknown): e is AxiosError<ApiError> {
  return (
    typeof e === 'object' &&
    e !== null &&
    'isAxiosError' in e &&
    !!(e as AxiosError<ApiError>).response?.data?.error
  )
}
